import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Indicator } from 'src/models/models';

@Component({
  selector: 'app-tree-select',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './tree-select.component.html',
  styleUrl: './tree-select.component.scss'
})
export class TreeSelectComponent implements OnInit {
  @Input() items: Indicator[] = [];
  @Input() selectedItems: string[] = [];
  @Output() selectionChange = new EventEmitter<string[]>();

  searchTerm: string = '';
  filteredItems: Indicator[] = [];
  expandedNodes: Set<string> = new Set();
  allIds: Set<string> = new Set(); // To keep track of all IDs for search results
  indeterminateNodes: Set<string> = new Set(); // Track nodes with partial selection

  constructor() { }

  ngOnInit(): void {
    this.filteredItems = [...this.items];
    this.updateAllIdsSet();
    this.updateIndeterminateStates();
  }

  ngOnChanges(): void {
    this.filteredItems = [...this.items];
    this.updateAllIdsSet();
    this.updateIndeterminateStates();
  }

  private updateAllIdsSet(): void {
    this.allIds.clear();
    this.items.forEach(item => {
      if (item.id) {
        this.allIds.add(item.id);
      }
    });
  }

  private updateIndeterminateStates(): void {
    this.indeterminateNodes.clear();

    // Process all parent nodes
    this.items.forEach(item => {
      if (item.id && this.hasChildren(item.id)) {
        this.checkIndeterminateState(item.id);
      }
    });
  }

  private checkIndeterminateState(nodeId: string): void {
    const childItems = this.getChildItems(nodeId);
    if (childItems.length === 0) return;

    // Check how many children are selected
    let selectedCount = 0;
    let hasIndeterminateChild = false;

    childItems.forEach(child => {
      if (child.id) {
        if (this.selectedItems.includes(child.id)) {
          selectedCount++;
        }
        if (this.indeterminateNodes.has(child.id)) {
          hasIndeterminateChild = true;
        }
      }
    });

    // Mark as indeterminate if some but not all children are selected,
    // or if any child is indeterminate
    if ((selectedCount > 0 && selectedCount < childItems.length) || hasIndeterminateChild) {
      this.indeterminateNodes.add(nodeId);
    } else {
      this.indeterminateNodes.delete(nodeId);
    }

    // Update parent nodes recursively
    const parentItem = this.items.find(i => i.id === nodeId)?.masterId;
    if (parentItem && parentItem !== '0') {
      this.checkIndeterminateState(parentItem);
    }
  }

  search(): void {
    if (!this.searchTerm) {
      this.filteredItems = [...this.items];
      return;
    }

    const term = this.searchTerm.toLowerCase();

    // First find all matching items
    const matchingItems = this.items.filter(item =>
      item.name.toLowerCase().includes(term)
    );

    // Get IDs of matches and their parents
    const matchAndParentIds = new Set<string>();

    // Add all matching items
    matchingItems.forEach(item => {
      if (item.id) matchAndParentIds.add(item.id);

      // Add all parent nodes to ensure they're visible
      let parentId = item.masterId;
      while (parentId && parentId !== '0') {
        matchAndParentIds.add(parentId);
        // Find the parent item
        const parentItem = this.items.find(i => i.id === parentId);
        parentId = parentItem?.masterId;
      }
    });

    // Filter items to include only matches and their parents
    this.filteredItems = this.items.filter(item =>
      item.id && matchAndParentIds.has(item.id)
    );

    // Expand all parent nodes that contain matches
    matchAndParentIds.forEach(id => {
      this.expandedNodes.add(id);
    });
  }

  toggleSelection(item: Indicator): void {
    if (!item.id) return;

    // Check if item is currently selected
    const isSelected = this.isSelected(item.id);

    if (isSelected) {
      // Deselect this item and all its children
      this.unselectWithChildren(item);
    } else {
      // Select this item and all its children
      this.selectWithChildren(item);

      // Make sure all parent nodes are expanded
      this.expandParents(item);
    }

    // Update the indeterminate states
    this.updateIndeterminateStates();

    this.selectionChange.emit([...this.selectedItems]);
  }

  private selectWithChildren(item: Indicator): void {
    if (!item.id) return;

    // Add this item if not already selected
    if (!this.selectedItems.includes(item.id)) {
      this.selectedItems.push(item.id);
    }

    // Select all children recursively
    this.getChildItems(item.id).forEach(child => {
      this.selectWithChildren(child);
    });
  }

  private unselectWithChildren(item: Indicator): void {
    if (!item.id) return;

    // Remove this item
    const index = this.selectedItems.indexOf(item.id);
    if (index > -1) {
      this.selectedItems.splice(index, 1);
    }

    // Unselect all children recursively
    this.getChildItems(item.id).forEach(child => {
      this.unselectWithChildren(child);
    });
  }

  private expandParents(item: Indicator): void {
    if (!item.masterId || item.masterId === '0') return;

    this.expandedNodes.add(item.masterId);

    // Find parent and expand it too
    const parentItem = this.items.find(i => i.id === item.masterId);
    if (parentItem) {
      this.expandParents(parentItem);
    }
  }

  isSelected(id: string): boolean {
    return this.selectedItems.includes(id);
  }

  isIndeterminate(id: string): boolean {
    return this.indeterminateNodes.has(id);
  }

  toggleNode(id: string): void {
    if (this.expandedNodes.has(id)) {
      this.expandedNodes.delete(id);
    } else {
      this.expandedNodes.add(id);
    }
  }

  isExpanded(id: string): boolean {
    return this.expandedNodes.has(id);
  }

  hasChildren(id: string): boolean {
    return this.items.some(item => item.masterId === id);
  }

  getChildItems(parentId: string): Indicator[] {
    return this.filteredItems.filter(item => item.masterId === parentId);
  }

  getRootItems(): Indicator[] {
    return this.filteredItems.filter(item => !item.masterId || item.masterId === '0');
  }
}
